import type { UnwrapRef } from 'vue'

import { createQueryKeys } from '@lukemorales/query-key-factory'

import type { UseQueryOptionsWithoutQueryFn } from '~/types/util/tanstack-query'

import { useQueryDocumentSummary } from './summary/useQueryDocumentSummary'
import { useQueryDocumentReminder } from './reminder/useQueryDocumentReminder'
import {
  type IncludableField,
  type ParametersGetAll,
  type ParametersGetAllInfinite,
  responseDataTypeName,
  useFetchDocument,
} from './useFetchDocument'

export const queryKeys = createQueryKeys(responseDataTypeName, {
  all: ({
    workspaceId,
    fieldsToInclude = [],
    params,
  }:
  {
    workspaceId: MaybeRef<string>
    fieldsToInclude?: string[]
    params?: MaybeRef<ParametersGetAll>
  },
  ) => ({
    queryKey: [
      workspaceId,
      ...(fieldsToInclude ? [{ fieldsToInclude: fieldsToInclude.sort() }] : []),
      ...(params ? [unref(params)] : []),
    ],
  }),

  allInfinite: ({
    workspaceId,
    fieldsToInclude = [],
    params,
  }:
  {
    workspaceId: MaybeRef<string>
    fieldsToInclude?: string[]
    params?: MaybeRef<ParametersGetAll>
  },
  ) => ({
    queryKey: [
      workspaceId,
      ...(fieldsToInclude ? [{ fieldsToInclude: fieldsToInclude.sort() }] : []),
      ...(params ? [unref(params)] : []),
    ],
  }),

  byId: ({
    id,
    workspaceId,
    fieldsToInclude = [],
  }:
  {
    id: MaybeRef<string>
    workspaceId: MaybeRef<string>
    fieldsToInclude?: string[]
  }) => ({
    queryKey: [
      id,
      workspaceId,
      ...(fieldsToInclude ? [{ fieldsToInclude: fieldsToInclude.sort() }] : []),
    ],
  }),

  byContractId: ({
    contractId,
    fieldsToInclude = [],
  }: {
    contractId: MaybeRef<string>
    fieldsToInclude?: string[]
  }) => ({
    queryKey: [
      contractId,
      ...(fieldsToInclude ? [{ fieldsToInclude: fieldsToInclude.sort() }] : []),
    ],
  }),
})

const all = <T extends IncludableField[] = []>(
  {
    params,
    fieldsToInclude,
    useQueryOptions = {},
  }: {
    params?: MaybeRef<ParametersGetAll>
    fieldsToInclude?: T
    useQueryOptions?: UseQueryOptionsWithoutQueryFn
  } = {},
) => {
  const { getAll } = useFetchDocument()
  const { activeWorkspaceId } = useAuth()

  const queryKey = computed(() => queryKeys.all({
    workspaceId: activeWorkspaceId as MaybeRef<string>,
    fieldsToInclude,
    params,
  }).queryKey)

  return useQuery(
    {
      queryKey,
      queryFn: () => getAll({
        params: unref(params),
        workspaceId: unref(activeWorkspaceId) ?? '',
        fieldsToInclude: fieldsToInclude ?? [],
      }),
      enabled: () => !!unref(activeWorkspaceId),
      ...useQueryOptions,
    },
  )
}
export type QueryDocumentAllCacheData = UnwrapRef<ReturnType<typeof all>['data']>

const allInfinite = <T extends IncludableField[] = []>(
  {
    params,
    fieldsToInclude,
    useQueryOptions = {},
  }: {
    params?: MaybeRef<ParametersGetAllInfinite>
    fieldsToInclude?: T
    useQueryOptions?: UseQueryOptionsWithoutQueryFn
  } = {},
) => {
  const { getAll } = useFetchDocument()
  const { activeWorkspaceId } = useAuth()

  const queryKey = computed(() => queryKeys.allInfinite({
    workspaceId: activeWorkspaceId as MaybeRef<string>,
    fieldsToInclude,
    params,
  }).queryKey)

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => getAll({
      params: {
        per_page: DEFAULT_DOCUMENTS_PER_PAGE, // can be overridden by the params
        ...toValue(params),
        page: pageParam,
      },
      workspaceId: unref(activeWorkspaceId) ?? '',
      fieldsToInclude: fieldsToInclude ?? [],
    }),
    enabled: () => !!unref(activeWorkspaceId),
    getNextPageParam: lastPage => lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined,
    initialPageParam: 1,
    ...useQueryOptions,
  })
}
export type QueryDocumentAllInfiniteCacheData = UnwrapRef<ReturnType<typeof allInfinite>['data']>

const byId = <T extends IncludableField[] = []>(
  {
    id,
    fieldsToInclude,
    useQueryOptions = {},
  }:
  {
    id: MaybeRef<string>
    fieldsToInclude?: T
    useQueryOptions?: UseQueryOptionsWithoutQueryFn
  },
) => {
  const { getById } = useFetchDocument()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.byId({
      id,
      fieldsToInclude,
      workspaceId: activeWorkspaceId as MaybeRef<string>,
    }),
    queryFn: () => getById({
      id: unref(id),
      fieldsToInclude,
      workspaceId: unref(activeWorkspaceId) || '',
    }),
    enabled: () => !!unref(activeWorkspaceId),
    select: ({ data }) => data,
    ...useQueryOptions,
  })
}
export type QueryDocumentByIdCacheData = Awaited<ReturnType<ReturnType<typeof useFetchDocument>['getById']>>

const byContractId = <const T extends IncludableField[] = []>({
  contractId,
  fieldsToInclude,
  useQueryOptions = {},
}: {
  contractId: MaybeRef<string>
  fieldsToInclude?: T
  useQueryOptions?: UseQueryOptionsWithoutQueryFn
}) => {
  const { getByContractId } = useFetchDocument()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.byContractId({ contractId, fieldsToInclude }),
    queryFn: () => getByContractId({
      contractId: unref(contractId),
      workspaceId: unref(activeWorkspaceId) || '',
      fieldsToInclude: fieldsToInclude ?? [],
    }),
    enabled: () => !!unref(activeWorkspaceId) && !!unref(contractId),
    ...useQueryOptions,
  })
}
export type QueryDocumentByContractIdCacheData = Awaited<ReturnType<ReturnType<typeof useFetchDocument>['getByContractId']>>

export const useQueryDocument = {
  all,
  allInfinite,
  byId,
  byContractId,
  summary: useQueryDocumentSummary,
  reminder: useQueryDocumentReminder,
}
